// Dashboard tabs are routed, so this list is the single source of truth for both
// the router (App.tsx) and the sidebar (DashboardPage.tsx). Adding a tab here is
// enough to give it a URL.
export const DASHBOARD_TABS = [
  'calendar',
  'posts',
  'composer',
  'drafts',
  'analytics',
  'inbox',
  'accounts',
  'team',
  'settings',
  'approvals',
  'subscription',
  'payments',
  'admin-users',
  'admin-activity',
  'admin-stats',
] as const;

export type DashboardTab = (typeof DASHBOARD_TABS)[number];

/**
 * Tabs that are reached from somewhere else rather than from their own sidebar
 * entry, and which entry should light up while they are open. The composer has
 * no nav row of its own — it is opened by "Create post" or from the Posts list,
 * and the design shows Posts highlighted throughout.
 */
const TAB_NAV_PARENT: Partial<Record<DashboardTab, DashboardTab>> = {};

export function getNavTabFor(tab: DashboardTab): DashboardTab {
  return TAB_NAV_PARENT[tab] ?? tab;
}

/**
 * Tabs only a superadmin may open. Because every entry above gets a URL for
 * free, these need an explicit guard — otherwise anyone typing /admin-users
 * lands on a page that renders while its requests 403 behind it. The API is
 * still the real boundary; this only decides what the router shows.
 */
export const ADMIN_TABS = ['admin-users', 'admin-activity', 'admin-stats'] as const;

export type AdminTab = (typeof ADMIN_TABS)[number];

export function isAdminTab(tab: DashboardTab): tab is AdminTab {
  return (ADMIN_TABS as readonly string[]).includes(tab);
}

export const DEFAULT_DASHBOARD_TAB: DashboardTab = 'composer';

export function getDashboardTabPath(tab: DashboardTab): string {
  return `/${tab}`;
}

/** Where a signed-in user lands: the dashboard's default tab, as a real URL. */
export const POST_LOGIN_PATH = getDashboardTabPath(DEFAULT_DASHBOARD_TAB);

export function isDashboardTab(value: string): value is DashboardTab {
  return (DASHBOARD_TABS as readonly string[]).includes(value);
}

// `/` is the dashboard root and renders the default tab, so an unmatched
// pathname falls back instead of rendering nothing.
export function resolveDashboardTab(pathname: string): DashboardTab {
  const segment = pathname.replace(/^\/+/, '').split('/')[0];
  return isDashboardTab(segment) ? segment : DEFAULT_DASHBOARD_TAB;
}
